import { Box, DrawerHeader, Stack, Tooltip } from '@chakra-ui/react';
import { Formik } from 'formik';
import { Children, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import useRequestNumberPort from 'pages/telephony/numbers/queries/useRequestNumberPort';

import FieldToolTip from 'components/forms/new/FieldToolTip';
import SectionHandler from 'components/forms/new/SectionHandler';
import Drawer from 'components/presentational/Drawer';
import { Button, Card } from 'components/v4';
import useExistingNumberForm from 'hooks/useExistingNumberDrawer';
import AgreementCheckbox from './formFields/AgreementCheckbox';
import Bill from './formFields/Bill';
import Carrier from './formFields/Carrier';
import Comments from './formFields/Comments';
import ContactField from './formFields/ContactField';
import Crn from './formFields/Crn';
import NumbersToPort from './formFields/NumbersToPort';
import PleaseNote from './formFields/PleaseNote';
import PreferredPortingDate from './formFields/PreferredPortingDate';

import {
    formErrorStyle,
    noBreadcrumbStyle,
    paddingInputStyle,
} from 'components/forms/new/formStyles';
import walkthroughIds from 'pages/telephony/numbers/walkthroughIds';
import CancelButton from 'components/tables/CancelButton';

const ExistingNumbersDrawer = ({ isOpen, onClose }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const [fileUpload, setFileUpload] = useState(null);
    const [submitData, setSubmitData] = useState(new FormData());
    const [date, setDate] = useState(new Date());
    const [isHovered, setIsHovered] = useState(false);
    const dispatch = useDispatch();
    const { bringExistingNumber } = walkthroughIds;
    const queryClient = useQueryClient();
    const sideDrawer = true;

    const { state, stateError } = useSelector((state) => {
        return { ...state.modal };
    });
    const { currentScope } = useSelector((state) => {
        return { ...state.login };
    });
    const { modalService } = useSelector((state) => ({
        modalService: state.modal.state?.service ?? null,
    }));
    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });

    const drawerHeaderStyle = { ...noBreadcrumbStyle, ...paddingInputStyle };

    const isDisabled = !state?.agreeterms;

    const handleCancelButton = () => {
        dispatch({
            type: 'RESET_MODAL',
        });
        setFileUpload(null);
        setDate(new Date());
        onClose();
    };
    const { formUpload, validateOnSubmit } = useExistingNumberForm({
        fileUpload,
    });

    const { mutate: requestNumberPort } = useRequestNumberPort(
        submitData,
        currentCompany,
        currentScope,
    );

    const finalFormSubmit = () => {
        const finalSubmit = formUpload();
        // reset error before submit
        dispatch({
            type: 'CHANGE_MODAL_BACKEND_ERROR',
            payload: null,
        });
        if (validateOnSubmit && !validateOnSubmit()) {
            dispatch({
                type: 'CHANGE_MODAL_BACKEND_ERROR',
                payload: {
                    name: 'Field Error',
                    message: 'There are some field errors.',
                },
            });
            // Reset agreement checkbox when validation fails
            setState({ agreeterms: false });
            toast.error('Please check all required fields and agree to terms.');
            return;
        }
        setSubmitData(finalSubmit);
        setTimeout(() => {
            requestNumberPort(
                {},
                {
                    onSuccess: () => {
                        toast.success(
                            'Number Port request submitted successfully!',
                        );
                        handleCancelButton();
                        queryClient.invalidateQueries({
                            queryKey: ['numbers'],
                        });
                    },
                },
            );
        }, 0);
    };

    const setState = (payload) => {
        dispatch({
            type: 'CHANGE_MODAL_STATE',
            payload,
        });
    };

    // change handlers
    const carrierChangeHandler = (e) => {
        setState({ carrier: e.target.value });
    };

    const agreetermsChangeHandler = () => {
        setState({
            agreeterms: !state?.agreeterms,
        });
    };

    const preloadNumbersChangeHandler = () => {
        setState({
            preloadNumbers: !state?.preloadNumbers,
        });
    };

    const commentsChangeHandler = (e) => {
        setState({ comments: e.target.value });
    };

    const billChangeHandler = (e) => {
        if (e.target.files[0].type === 'application/pdf') {
            setFileUpload(e.target.files[0]);
            if (import.meta.env.MODE === 'development') {
                console.log('fileUpload!', fileUpload);
            }
        } else {
            window.alert('File type not supported');
        }
    };

    const abnChangeHandler = (e) => {
        setState({ abn: e.target.value });
    };

    const authNameChangeHandler = (e) => {
        setState({
            authorizedSignerName: e.target.value,
        });
    };

    const authEmailChangeHandler = (e) => {
        setState({
            authorizedSignerEmail: e.target.value,
        });
    };

    const authContactChangeHandler = (e) => {
        setState({
            authorizedSignerNumber: e.target.value,
        });
    };

    const handleClose = () => {
        setFileUpload(null);
        onClose();
    };

    const formFields = (errors, touched, values, props) => {
        const formProps = {
            state,
            stateError,
            setState,
            darkMode,
            errors,
            touched,
            values,
            props,
        };
        const fields = [
            <Box key="carrier-bill-abn-porting_date">
                <Carrier
                    key="carrier"
                    onChange={carrierChangeHandler}
                    walkthroughId={bringExistingNumber.carrier}
                    {...formProps}
                />
                <Bill
                    key="bill"
                    fileUpload={fileUpload}
                    onChange={billChangeHandler}
                    walkthroughId={bringExistingNumber.fileUpload}
                    {...formProps}
                />
                <Crn
                    key="abn"
                    onChange={abnChangeHandler}
                    walkthroughId={
                        bringExistingNumber.companyRegistrationNumber
                    }
                    {...formProps}
                />
                <PreferredPortingDate
                    key="preferredPortingDate"
                    date={date}
                    setDate={setDate}
                    {...formProps}
                />
            </Box>,
            <Box key="authorized-fields" sx={formErrorStyle}>
                <ContactField
                    key="authorizedSignerName"
                    label="Authorized Contact Full Name"
                    field="authorizedSignerName"
                    tooltipLabel="Name of contact"
                    onChange={authNameChangeHandler}
                    walkthroughId={bringExistingNumber.authorisedSignerFullName}
                    {...formProps}
                />
                <ContactField
                    key="authorizedSignerEmail"
                    label="Authorized Contact Email"
                    field="authorizedSignerEmail"
                    tooltipLabel="Email of contact"
                    onChange={authEmailChangeHandler}
                    walkthroughId={bringExistingNumber.authorisedSignerEmail}
                    {...formProps}
                />
                <ContactField
                    key="authorizedSignerNumber"
                    label="Authorized Contact Number"
                    field="authorizedSignerNumber"
                    tooltipLabel="Phone Number of Contact"
                    onChange={authContactChangeHandler}
                    walkthroughId={bringExistingNumber.authorisedSignerContact}
                    {...formProps}
                />
            </Box>,
            <Box key="NumbersToPort">
                <NumbersToPort
                    key="numbersToPort"
                    setState={setState}
                    {...formProps}
                />
            </Box>,
            <Box key="agreements section">
                <AgreementCheckbox
                    key="preloadNumbers"
                    label="Preload Numbers Agreement"
                    field="preloadNumbers"
                    subText="I require the numbers to be loaded into the portal as soon as they have been validated"
                    onChange={preloadNumbersChangeHandler}
                    walkthroughId={bringExistingNumber.preloadNumbers}
                    {...formProps}
                />
                <Comments
                    key="comments"
                    onChange={commentsChangeHandler}
                    walkthroughId={bringExistingNumber.comments}
                    {...formProps}
                />
                <AgreementCheckbox
                    key="agreeterms"
                    label="Agreement"
                    field="agreeterms"
                    subText="
                                            I understand that I am authorising
                                            the listed number(s) to be ported on
                                            behalf of the company.
        "
                    onChange={agreetermsChangeHandler}
                    walkthroughId={bringExistingNumber.agreement}
                    {...formProps}
                />
            </Box>,
            <PleaseNote
                key="pleaseNote"
                darkMode={darkMode}
                subText={`Porting requests can take 20 days to process. A representative
                will be in touch to discuss scheduling.`}
            />,
        ];
        return fields;
    };

    // opens a modal and sets hash when the component loads
    useEffect(() => {
        if (state?.aggregateTable == undefined && isOpen) {
            dispatch({
                type: 'OPEN_MODAL',
                payload: {
                    mode: 'Edit',
                    state: {
                        service: modalService,
                        preferredDate: Date.now(),
                        aggregateTable: [{ id: 1, from: '', to: '' }],
                    },
                    hash: '',
                    loading: false,
                    show: true,
                },
            });
            dispatch({
                type: 'CHANGE_MODAL_HASH',
            });
        }
    }, [state?.aggregateTable, dispatch, modalService, isOpen]);

    return (
        <Drawer isOpen={isOpen} onClose={handleClose}>
            <DrawerHeader pt={0} sx={drawerHeaderStyle}>
                Bring Existing Numbers
            </DrawerHeader>
            <Formik
                initialValues={submitData}
                enableReinitialize
                onSubmit={(value) => {
                    finalFormSubmit(value);
                }}
                validateOnChange>
                {({ errors, touched, handleSubmit, values, ...props }) => (
                    <form onSubmit={handleSubmit}>
                        <Stack spacing={30}>
                            <Card
                                borderRadius={15}
                                padding="0px 30px"
                                maxWidth={895}
                                ml={0}
                                bg={darkMode ? 'dark.tcap' : null}>
                                <Stack sx={{ margin: '0 !important' }}>
                                    {Children.map(
                                        formFields(
                                            errors,
                                            touched,
                                            values,
                                            props,
                                        ),
                                        (child, index) => (
                                            <SectionHandler
                                                index={index}
                                                child={child}
                                                count={formFields()?.length}
                                            />
                                        ),
                                    )}
                                </Stack>
                            </Card>

                            <Stack
                                direction="row"
                                sx={{
                                    paddingBottom: sideDrawer ? 3 : 10,
                                    marginLeft: sideDrawer && 0,
                                    marginTop: sideDrawer && 25,
                                    paddingTop: sideDrawer && 3,
                                    position: sideDrawer ? 'sticky' : 'static',
                                    left: sideDrawer ? 0 : 'auto',
                                    bottom: sideDrawer ? -35 : 'auto',
                                    backgroundColor:
                                        sideDrawer &&
                                        (darkMode
                                            ? 'var(--chakra-colors-dark-bgDark)'
                                            : '#F5F5F5'),
                                    zIndex: 1,
                                    width: '100%',
                                }}>
                                <div
                                    className="edit-form-buttons"
                                    style={{
                                        border: 'none',
                                        textAlign: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <Button
                                        type="submit"
                                        data-walkthroughid="/bringExistingNumber/button/save"
                                        className="h-10 min-w-[5rem] font-semibold disabled:cursor-not-allowed"
                                        sx={{ width: 142 }}
                                        isDisabled={isDisabled}>
                                        <Tooltip
                                            hasArrow
                                            label={
                                                isDisabled
                                                    ? 'Please fill in the form and agree to the terms.'
                                                    : ''
                                            }
                                            aria-label="disabled tooltip for available numbers submit button">
                                            <span>Save</span>
                                        </Tooltip>
                                    </Button>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        flex: 1,
                                    }}
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}>
                                    <CancelButton
                                        onClick={handleCancelButton}
                                    />

                                    {isHovered && (
                                        <FieldToolTip
                                            label="Close will clear the form"
                                            ariaLabel="close-button-tip"
                                            left="30px"
                                            top="13px"
                                        />
                                    )}
                                </div>
                            </Stack>
                        </Stack>
                    </form>
                )}
            </Formik>
        </Drawer>
    );
};

export default ExistingNumbersDrawer;
